import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { Plus, Trash2, FileText, BookOpen, PenLine, Upload, ArrowLeft, Loader2 } from 'lucide-react';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CourseMaterialsPage() {
  const [currentUser, setCurrentUser] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedYear, setSelectedYear] = useState('13');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [teacherClasses, setTeacherClasses] = useState([]);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formType, setFormType] = useState('note');
  const [formContent, setFormContent] = useState('');
  const [formFile, setFormFile] = useState(null);

  useEffect(() => {
    const user = getCurrentUser();
    setCurrentUser(user);
    if (user) loadClasses(user);
  }, []);

  const loadClasses = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/classes/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const classes = await res.json();
        const myClasses = classes.filter(c => 
          c.teacher_id === user.id || user.role === 'principal' || user.role === 'admin'
        );
        setTeacherClasses(myClasses);
        if (myClasses.length > 0) {
          setSelectedSubject(myClasses[0].subject);
          setSelectedYear(String(myClasses[0].year));
        }
      }
    } catch (e) {
      console.error('Error loading classes:', e);
    }
  };

  useEffect(() => {
    if (selectedSubject && selectedYear) loadMaterials();
  }, [selectedSubject, selectedYear]);

  const loadMaterials = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/course-materials/teacher/materials?token=${token}&subject=${encodeURIComponent(selectedSubject)}&year_level=${selectedYear}`
      );
      if (res.ok) {
        setMaterials(await res.json());
      }
    } catch (e) {
      console.error('Error loading materials:', e);
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!formTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formContent.trim() && !formFile) {
      toast.error('Please enter content or upload a file');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      
      if (formFile) {
        // File upload
        const formData = new FormData();
        formData.append('token', token);
        formData.append('subject', selectedSubject);
        formData.append('year_level', selectedYear);
        formData.append('material_type', formType);
        formData.append('title', formTitle);
        formData.append('content', formContent);
        formData.append('file', formFile);

        const res = await fetch(`${API_URL}/api/course-materials/teacher/upload-material`, {
          method: 'POST',
          body: formData
        });
        if (!res.ok) throw new Error('Upload failed');
      } else {
        // Text only
        const params = new URLSearchParams({
          token, subject: selectedSubject, year_level: selectedYear,
          material_type: formType, title: formTitle, content: formContent
        });
        const res = await fetch(`${API_URL}/api/course-materials/teacher/material?${params}`, {
          method: 'POST'
        });
        if (!res.ok) throw new Error('Create failed');
      }

      toast.success('Course material created!');
      setShowCreate(false);
      setFormTitle('');
      setFormContent('');
      setFormFile(null);
      setFormType('note');
      loadMaterials();
    } catch (e) {
      toast.error('Failed to create material');
    }
    setCreating(false);
  };

  const handleDelete = async (materialId) => {
    if (!confirm('Delete this material?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/course-materials/teacher/material/${materialId}?token=${token}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        toast.success('Material deleted');
        loadMaterials();
      }
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const uniqueSubjects = [...new Set(teacherClasses.map(c => c.subject))];
  const uniqueYears = [...new Set(teacherClasses.filter(c => c.subject === selectedSubject).map(c => String(c.year)))];

  const typeIcons = {
    note: <BookOpen className="h-4 w-4 text-blue-600" />,
    exercise: <PenLine className="h-4 w-4 text-green-600" />,
    assignment: <FileText className="h-4 w-4 text-purple-600" />
  };

  const typeColors = {
    note: 'bg-blue-50 text-blue-700 border-blue-200',
    exercise: 'bg-green-50 text-green-700 border-green-200',
    assignment: 'bg-purple-50 text-purple-700 border-purple-200'
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="course-materials-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Course Materials</h1>
          <p className="text-gray-500 text-sm mt-1">Create and manage notes, exercises, and assignments for your students</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-material-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Material
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="w-48">
          <label className="text-sm font-medium mb-1 block">Subject</label>
          <Select value={selectedSubject} onValueChange={setSelectedSubject}>
            <SelectTrigger><SelectValue placeholder="Select subject" /></SelectTrigger>
            <SelectContent>
              {uniqueSubjects.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-32">
          <label className="text-sm font-medium mb-1 block">Year Level</label>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {uniqueYears.map(y => (
                <SelectItem key={y} value={y}>Year {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Materials List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : materials.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BookOpen className="h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">No course materials yet</p>
            <p className="text-gray-400 text-sm mt-1">Click "Add Material" to create your first one</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {materials.map(m => (
            <Card key={m.id} className="hover:shadow-md transition-shadow" data-testid={`material-${m.id}`}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  {typeIcons[m.material_type] || <FileText className="h-4 w-4" />}
                  <div>
                    <h3 className="font-medium text-gray-900">{m.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${typeColors[m.material_type] || ''}`}>
                        {m.material_type}
                      </span>
                      <span className="text-xs text-gray-400">Year {m.year_level}</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(m.id)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-material-${m.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Material Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg" data-testid="create-material-dialog">
          <DialogHeader>
            <DialogTitle>Add Course Material</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Title</label>
              <Input
                placeholder="e.g., Week 3: Introduction to Databases"
                value={formTitle}
                onChange={e => setFormTitle(e.target.value)}
                data-testid="material-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Type</label>
              <Select value={formType} onValueChange={setFormType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="note">Note</SelectItem>
                  <SelectItem value="exercise">Exercise</SelectItem>
                  <SelectItem value="assignment">Assignment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Content</label>
              <Textarea
                placeholder="Enter the material content here..."
                value={formContent}
                onChange={e => setFormContent(e.target.value)}
                rows={6}
                data-testid="material-content-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">
                <Upload className="h-4 w-4 inline mr-1" />
                Attach File (optional)
              </label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
                onChange={e => setFormFile(e.target.files[0])}
                data-testid="material-file-input"
              />
              {formFile && (
                <p className="text-xs text-emerald-600 mt-1">{formFile.name}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="create-material-submit"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              {creating ? 'Creating...' : 'Create Material'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
